import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { USER_ROLES } from 'src/common/constants';
import Button from '../Button/Button';
import { GenericDialogue } from '../Dialogue/Dialogue';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import ContactFormSkeleton from './skeletons/ContactFormSkeleton';
import { toast } from 'react-toastify';
import ContactFormContent from '../ContactFormComponents/ContactFormContent';
import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';
import {
  ContactFormActionsContext,
  ContactFormDataContext,
  useContactFormActionsFromContext,
  useContactFormDataFromContext,
} from 'src/SharedCommonContext/ContactFormContext';
import { getRoleDisplayLabel } from 'src/common/roleRegistry/roleRegistryHelpers';
import {
  PropertiesActionsContext,
  usePropertiesActionsFromContext,
  usePropertiesDataFromContext,
} from 'src/pages/Contacts/ContactsContext/PropertiesForContactsContext';
import { retrieveServiceProviderPropertiesFromApi } from 'src/pages/SharedPageCustomHooks/UsePropertySuiteSelection/helpers';
import { useFetchServicesProvidedTypes } from 'src/SharedCustomHooks/UseContactFormOptions/useContactFormOptions';
import {
  UseContactsReturn,
  useRetrieveContactsFromAPI,
} from 'src/SharedCustomHooks/UseFetchContacts/useFetchContacts';
import 'react-toastify/dist/ReactToastify.css';
import {
  normalizePropertyKeys,
  sanitizeContactData,
} from 'src/components/ContactForm/helpers';

interface ContactFormProps {
  onClose: () => void;
  onSubmit: (data: IContactFormData, actionType: string) => void;
  sendResetPasswordLinkToUser: (contactData: IContactFormData) => void;
  sendActivationLinkToUser: (contactData: IContactFormData) => void;
  pageUsedFrom?: string;
  allowedRoles?: string[];
  editingExistingContactInTable?: boolean;
  /* Todo: prop threading this isn't the cleanest implementation, but my hand is kind of forced by the API structure for adding existing homeowners to suites.
   * We need to know the current suite ID when adding an existing homeowner from the suite users page. We should fix this properly when we refactor the contact form context and API handling. */
  currentSuiteIdForAddingExistingHomeowner?: string;
  onRoleChangeSuccess?: () => void;
}

const ContactForm = ({
  onClose,
  onSubmit,
  sendResetPasswordLinkToUser,
  sendActivationLinkToUser,
  allowedRoles,
  pageUsedFrom,
  editingExistingContactInTable,
  currentSuiteIdForAddingExistingHomeowner,
  onRoleChangeSuccess,
}: ContactFormProps) => {
  const { contactFormData, isLoading }: ContactFormDataContext =
    useContactFormDataFromContext();

  const {
    setContactFormData,
    clearContactFormData,
    validateContactFormData,
    isSubmitDisabled,
  }: ContactFormActionsContext = useContactFormActionsFromContext();

  // TODO: Remove client-side duplicate checking once server-side validation is fully implemented.
  const {
    homeOwnersRealtorsAndOccupantContacts,
    propertyDevelopersAndServiceManagerContacts,
    isLoading: isLoadingContacts,
  }: UseContactsReturn = useRetrieveContactsFromAPI();

  const { servicesProvidedTypes, isLoading: isLoadingTypes } =
    useFetchServicesProvidedTypes();

  const [formType, setFormType] = useState<string>('New');

  const user: any = useSelector(selectAuthenticatedUser);

  const { availableData, loading: propertiesLoading } =
    usePropertiesDataFromContext();

  const {
    fetchHomeownerProperties,
    fetchPropertiesSuites,
    fetchPropertySuites,
    fetchServiceProviderManagerProperties,
  }: PropertiesActionsContext = usePropertiesActionsFromContext();

  useEffect(() => {
    const onMount = async () => {
      await initializeContactForm();
    };

    onMount();

    return () => {
      clearContactFormData();
    };
  }, []);

  // Handle role-specific data loading when role changes.
  useEffect(() => {
    if (contactFormData.id) return;

    loadDataForNewContact();
  }, [contactFormData.userRole, contactFormData.id, user.id, user.roles]);

  const initializeContactForm = async (): Promise<void> => {
    if (!contactFormData.id) {
      setContactFormData({ userRole: 'default' });
      return;
    }

    const userRole = contactFormData?.roles?.[0]?.role?.name || '';

    const isFromPropertyOrSuite: boolean =
      pageUsedFrom === 'Properties' || pageUsedFrom === 'Suites';

    if (!userRole) {
      const defaultRole = isFromPropertyOrSuite
        ? 'Property Developer'
        : 'default';
      setContactFormData({ userRole: defaultRole });
      return;
    }

    try {
      await initializeByRole(userRole);
    } catch (error) {
      console.error('Error initializing contact form:', error);
    }
  };

  const loadDataForNewContact = async (): Promise<void> => {
    if (!contactFormData.userRole || contactFormData.userRole === 'default') {
      return;
    }

    try {
      if (isServiceProviderOrPropertyDeveloper(contactFormData.userRole)) {
        await fetchServiceProviderManagerProperties();
        return;
      }

      if (!requiresPropertyData(contactFormData.userRole)) {
        return;
      }

      await fetchHomeownerProperties();

      if (
        !requiresSuiteData(contactFormData.userRole) ||
        !availableData.availableProperties?.length
      ) {
        return;
      }

      await loadSuitesForNewContact();
    } catch (error) {
      console.error('Error loading data for new contact:', error);
    }
  };

  const initializeByRole = async (userRole: string): Promise<void> => {
    if (userRole === USER_ROLES.OCCUPANT) {
      await initializeOccupantRole();
      return;
    }

    if (isHomeOwnerOrRealtorOrPropertyDeveloper(userRole)) {
      await initializePropertyOwnerRoles();
      return;
    }

    if (userRole === USER_ROLES.SERVICE_MANAGER) {
      await initializeServiceManagerRole();
      return;
    }

    if (userRole === USER_ROLES.SERVICE_PROVIDER) {
      await initializeServiceProviderRole();
      return;
    }

    // Default case: just set the role
    setContactFormData({ userRole });
  };

  const initializeOccupantRole = async (): Promise<void> => {
    await fetchHomeownerProperties();

    if (contactFormData.propertyId) {
      await fetchPropertySuites(contactFormData.propertyId);
    }

    const suites = mapUserSuitesToSuiteList(
      contactFormData.userSuites,
      contactFormData.propertyId
    );
    setContactFormData({
      Suites: suites,
      userRole: USER_ROLES.OCCUPANT,
    });
  };

  const initializePropertyOwnerRoles = async (): Promise<void> => {
    const userRole: string = contactFormData?.roles?.[0]?.role?.name || '';
    setContactFormData({ userRole });

    await fetchHomeownerProperties();

    const properties: any[] = getUniquePropertiesFromContactData();

    await loadSuitesForPropertyOwners(properties);
  };

  const initializeServiceManagerRole = async (): Promise<void> => {
    await fetchServiceProviderManagerProperties();
    setContactFormData({ userRole: USER_ROLES.SERVICE_MANAGER });

    if (contactFormData.propertyId) {
      await fetchPropertySuites(contactFormData.propertyId);
    }
  };

  const initializeServiceProviderRole = async (): Promise<void> => {
    // Guard clause to catch the rare edge case the contact form data isn't loaded properly. Just leave the properties empty.
    if (!contactFormData.id) {
      setContactFormData({
        userRole: USER_ROLES.SERVICE_PROVIDER,
        serviceType: contactFormData.serviceType || '',
        properties: [],
      });

      return;
    }

    await fetchServiceProviderManagerProperties();

    const propertiesData: any = await retrieveServiceProviderPropertiesFromApi(
      contactFormData.id
    );

    const normalizedProperties: any[] =
      normalizePropertyKeys(propertiesData) || [];

    const newContactFormState = {
      userRole: USER_ROLES.SERVICE_PROVIDER,
      serviceType: contactFormData.serviceType,
      properties: normalizedProperties,
    };

    setContactFormData(newContactFormState);
  };

  const loadSuitesForNewContact = async (): Promise<void> => {
    const propertyIds = availableData.availableProperties.map(
      (p: any) => p.propertyId || p.id
    );

    const params = {
      Properties: propertyIds,
      UserId: user.id,
      Role: user.roles[0].role.name,
    };

    await fetchPropertiesSuites(params);
  };

  const loadSuitesForPropertyOwners = async (
    properties: any[]
  ): Promise<void> => {
    if (properties.length === 0) {
      setContactFormData({ Suites: [] });
      return;
    }

    const params = {
      Properties: properties.map((x) => x.propertyId),
      UserId: user.id,
      Role: user.roles[0].role.name,
    };

    await fetchPropertiesSuites(params);

    const suites = mapUserSuitesToSuiteListWithPropertyNames(
      contactFormData.userSuites || []
    );

    setContactFormData({ Suites: suites });
  };

  const handleValidateAndSubmit = (actionType: string): void => {
    const { isValid, errors } = validateContactFormData();

    if (!isValid) {
      const errorMessages: string = Object.values(errors).join('. ');

      toast.error(`Please fix validation errors: ${errorMessages}`, {
        autoClose: 8000,
        position: 'top-center',
      });

      return;
    }

    const sanitizedData: IContactFormData =
      sanitizeContactData(contactFormData);

    onSubmit(sanitizedData, formType);
  };

  const isServiceProviderOrPropertyDeveloper = (role: string): boolean => {
    return [
      USER_ROLES.SERVICE_PROVIDER,
      USER_ROLES.PROPERTY_DEVELOPER,
    ].includes(role);
  };

  const requiresPropertyData = (role: string): boolean => {
    return ['Home Owner', 'Realtor', 'Occupant', 'Service Manager'].includes(
      role
    );
  };

  const requiresSuiteData = (role: string): boolean => {
    return ['Home Owner', 'Realtor'].includes(role);
  };

  const isHomeOwnerOrRealtorOrPropertyDeveloper = (
    userRole: string
  ): boolean => {
    return [
      USER_ROLES.HOME_OWNER,
      USER_ROLES.REALTOR,
      USER_ROLES.PROPERTY_DEVELOPER,
    ].includes(userRole);
  };

  const mapUserSuitesToSuiteList = (
    userSuites: any[] = [],
    propertyId: string = ''
  ): any[] => {
    return userSuites.map((suite) => ({
      Id: suite.suiteId,
      name: suite.suiteName,
      propertyId,
    }));
  };

  const mapUserSuitesToSuiteListWithPropertyNames = (
    userSuites: any[] = []
  ): any[] => {
    return userSuites.map((suite) => ({
      Id: suite.suiteId,
      name: `${suite.propertyName} - ${suite.suiteName}`,
      propertyId: suite.propertyId,
    }));
  };

  const getUniquePropertiesFromContactData = (): any[] => {
    if (!contactFormData.properties?.length) {
      return [];
    }

    const properties = contactFormData.properties.map((property: any) => ({
      propertyId: property.propertyId,
      name: property.name,
    }));

    return properties.filter(
      (item: any, index: any, self: any) =>
        index === self.findIndex((p: any) => p.propertyId === item.propertyId)
    );
  };

  const showLoader = (): boolean => {
    return isLoading || isLoadingContacts || isLoadingTypes;
  };

  // Scope to role-driven property fetches only. propertiesSuites/propertySuite
  // also fire when the user picks a property/suite in-section, which must not
  // collapse those controls into a skeleton mid-interaction.
  const isRoleDataLoading: boolean =
    propertiesLoading.homeownerProperties ||
    propertiesLoading.serviceProviderProperties;

  const getFormHeadline = (): string => {
    const isFromPropertyOrSuite: boolean =
      pageUsedFrom === 'Properties' || pageUsedFrom === 'Suites';

    if (isFromPropertyOrSuite && !editingExistingContactInTable) {
      return 'Add a Contact';
    }

    if (isFromPropertyOrSuite && editingExistingContactInTable) {
      return 'Edit Contact';
    }

    return (contactFormData.id ? 'Edit' : 'Add a') + ' Contact';
  };

  return (
    <GenericDialogue
      isOpen
      onClose={onClose}
      maxWidth="155rem"
      minHeight='97vh'
      title={getFormHeadline()}
      footer={
        <div className="sendResetBtn">
          {contactFormData.id && pageUsedFrom === 'Contacts' ? (
            contactFormData.status !== 'Active' ? (
              <Button
                color="teal"
                size="small"
                label="Send Activation Link"
                disabled={isSubmitDisabled()}
                onClick={() => sendActivationLinkToUser(contactFormData)}
                style={{ marginRight: '1.5rem' }}
              />
            ) : (
              <Button
                color="teal"
                size="small"
                label="Send Reset Password Link"
                disabled={isSubmitDisabled()}
                onClick={() => sendResetPasswordLinkToUser(contactFormData)}
                style={{ marginRight: '1.5rem' }}
              />
            )
          ) : (
            <Button
              color="semantic"
              size="small"
              label="Save and Add Another"
              disabled={isSubmitDisabled()}
              onClick={() => handleValidateAndSubmit('saveAndAdd')}
              style={{ paddingRight: '3rem' }}
            />
          )}

          <Button
            color="teal"
            size="small"
            label={'Save Contact'}
            disabled={isSubmitDisabled()}
            onClick={() => handleValidateAndSubmit('saveContact')}
          />
        </div>
      }
    >
      {showLoader() ? (
        <ContactFormSkeleton />
      ) : (
        <ContactFormContent
          types={servicesProvidedTypes}
          pageUsedFrom={pageUsedFrom}
          contactType={(role: string) => getRoleDisplayLabel(role)}
          user={user}
          allowedRoles={allowedRoles}
          homeOwnersRealtorsAndOccupantContacts={
            homeOwnersRealtorsAndOccupantContacts
          }
          propertyDevelopersAndServiceManagerContacts={
            propertyDevelopersAndServiceManagerContacts
          }
          editingExistingContactInTable={editingExistingContactInTable}
          formType={formType}
          setFormType={setFormType}
          currentSuiteIdForAddingExistingHomeowner={
            currentSuiteIdForAddingExistingHomeowner
          }
          isRoleDataLoading={isRoleDataLoading}
          onRoleChangeSuccess={onRoleChangeSuccess}
        />
      )}
    </GenericDialogue>
  );
};

export default ContactForm;
